/**
 * @file shared/fechas/fecha-like.ts
 * @description FUENTE ÚNICA DE VERDAD para normalizar fechas que llegan de Firestore
 * (Timestamp del cliente o de admin, string ISO, epoch numérico, o ya un Date nativo).
 * Reemplaza el patrón repetido en ~15 archivos:
 * `(x as any)?.toDate ? (x as any).toDate() : new Date((x as any).seconds * 1000)`.
 * REGLA DURA: TypeScript puro — sin imports de firebase, DOM ni Node.
 */

/** Cualquier forma en la que puede llegar una fecha guardada en Firestore. */
export type FirestoreDateLike =
  | Date
  | string
  | number
  | { toDate(): Date }
  | { seconds: number }
  | null
  | undefined;

function hasToDateFn(v: object): v is { toDate(): Date } {
  return typeof (v as { toDate?: unknown }).toDate === "function";
}

function hasSeconds(v: object): v is { seconds: number } {
  return typeof (v as { seconds?: unknown }).seconds === "number";
}

/**
 * Normaliza un valor tipo-fecha de Firestore a `Date`, o `null` si no se puede.
 * El llamador decide su propio fallback: `toJsDateOrNull(x) ?? new Date()`.
 */
export function toJsDateOrNull(v: FirestoreDateLike): Date | null {
  if (v == null) return null;
  if (v instanceof Date) return v;
  if (typeof v === "object") {
    if (hasToDateFn(v)) return v.toDate();
    if (hasSeconds(v)) return new Date(v.seconds * 1000);
    return null;
  }
  // string | number
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/** Igual que `toJsDateOrNull` pero en milisegundos, con el fallback que ya tenía cada call-site. */
export function toMillisOr(v: FirestoreDateLike, fallback: number): number {
  const d = toJsDateOrNull(v);
  return d ? d.getTime() : fallback;
}
